import { useCallback, useMemo, useRef, useState } from 'react';
import config from '@config/index';
import { useVendor } from '@context/VendorContext';
import { useSnackbar } from '@goodfynd/context';
import type { DrawerRefMethods } from '@goodfynd/hooks';
import { FlexView, Text } from '@goodfynd/ui';
import { Button } from '@goodfynd/ui';
import { useConfirmationDialog } from '@goodfynd/ui';
import { useInfiniteQueryResults } from '@hooks/api/useInfiniteQueryResults';
import { useLocationPermission } from '@hooks/useLocationPermission';
import type { ScheduleAddResponse } from '@services/api/types';
import { mapGeoResult } from '@utils/geo-util';
import { dateUtil } from '@utils/index';

import { useCreateSchedule } from '../../hooks/useCreateSchedule';
import { ScheduleWizardDrawer } from '../schedule-wizard';

import { OneClickSchedulePreview } from './OneClickSchedulePreview';
import type {
  OneClickScheduleButtonProps,
  OneClickScheduleData,
} from './types';

export function OneClickScheduleButton({
  className,
  onScheduleCreated,
  queryKey,
}: OneClickScheduleButtonProps) {
  const [isBusy, setIsBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState<OneClickScheduleData | null>(
    null
  );
  const [wizardInitialStep, setWizardInitialStep] = useState(2);
  const wizardDrawerRef = useRef<DrawerRefMethods>(null);
  const { openSnackbarWarning } = useSnackbar();
  const { vendor } = useVendor();

  const { getConfirmation } = useConfirmationDialog();

  const handleOpenWizard = useCallback(() => {
    wizardDrawerRef.current?.open();
  }, []);

  const handleShowLocationAlert = useCallback(
    async (type: 'denied' | 'unsupported' | 'prompt') => {
      const messages = {
        denied: {
          title: 'Location Permission Denied',
          message: (
            <FlexView direction="column" gap={16}>
              <Text as="body2" tag="span">
                Location permission has been denied. To use this feature, please
                enable location access in your browser settings.
              </Text>
              <Text as="body2" tag="span">
                You can also manually enter your location using the schedule
                wizard.
              </Text>
            </FlexView>
          ),
        },
        unsupported: {
          title: 'Location Not Supported',
          message: (
            <FlexView direction="column" gap={16}>
              <Text as="body2" tag="span">
                Geolocation is not supported by your browser. Please use a
                different browser or manually enter your location using the
                schedule wizard.
              </Text>
            </FlexView>
          ),
        },
        prompt: {
          title: 'Location Permission Required',
          message: (
            <FlexView direction="column" gap={16}>
              <Text as="body2" tag="span">
                To create a schedule at your current location, we need access to
                your location. Please grant location permission when prompted.
              </Text>
              <Text as="body2" tag="span">
                You can also manually enter your location using the schedule
                wizard if you prefer not to share your location.
              </Text>
            </FlexView>
          ),
        },
      };

      const shouldOpenWizard = await getConfirmation({
        cancelButtonText: 'Dismiss',
        confirmButtonText: 'Open Wizard',
        title: messages[type].title,
        message: messages[type].message,
      });

      if (shouldOpenWizard) {
        handleOpenWizard();
      }
    },
    [getConfirmation, handleOpenWizard]
  );

  const customMessages = useMemo(
    () => ({
      denied: null,
      unsupported: null,
      prompt: null,
    }),
    []
  );

  const handlePermissionDenied = useCallback(() => {
    handleShowLocationAlert('denied');
  }, [handleShowLocationAlert]);

  const { checkAndRequestPermission, requestLocation } = useLocationPermission({
    customMessages,
    onPermissionDenied: handlePermissionDenied,
  });
  const { unshiftData } = useInfiniteQueryResults(queryKey);
  const handleCreateScheduleSuccess = useCallback(
    (response: ScheduleAddResponse) => {
      setDialogOpen(false);
      setScheduleData(null);
      onScheduleCreated?.();
      unshiftData([response.schedule[0]]);
    },
    [onScheduleCreated, unshiftData]
  );

  const { createSchedule, isCreatingSchedule } = useCreateSchedule({
    getSuccessMessage: () =>
      "🎉 Congratulations! You've been added to the public Goodfynd map.",
    onSuccess: handleCreateScheduleSuccess,
  });

  const getNearestPastHalfHour = useCallback((date: Date): Date => {
    // Round down to nearest 30 minutes
    let rounded = dateUtil.roundToNearestMinutes(date, {
      nearestTo: 30,
      roundingMethod: 'floor',
    });

    // If we're exactly on a half-hour boundary, go back 30 minutes to ensure we're in the past
    if (rounded.getTime() === date.getTime()) {
      rounded = dateUtil.addMinutes(rounded, -30);
    }

    return rounded;
  }, []);

  const reverseGeocode = useCallback(
    async (
      latitude: number,
      longitude: number
    ): Promise<JsonLocation | null> => {
      try {
        const geocoder = new google.maps.Geocoder();
        const response = await geocoder.geocode({
          location: { lat: latitude, lng: longitude },
        });

        if (response.results && response.results.length > 0) {
          return mapGeoResult(response.results[0]);
        }

        return null;
      } catch (error) {
        console.error('Reverse geocoding error:', error);
        return null;
      }
    },
    []
  );

  const handleRequestLocationAndCreateSchedule = useCallback(async () => {
    setIsBusy(true);
    try {
      // Get user's current location
      const position = await requestLocation();
      const { latitude, longitude } = position.coords;

      // Reverse geocode to get address
      const location = await reverseGeocode(latitude, longitude);

      if (!location) {
        openSnackbarWarning({
          message: 'Unable to determine your address. Please try again.',
        });
        return;
      }

      // Calculate nearest past half hour and end time (start + 3 hours)
      const now = new Date();
      const startTime = getNearestPastHalfHour(now);
      const endTime = dateUtil.addHours(startTime, 3);

      // Show dialog with schedule preview
      setScheduleData({
        location,
        startTime,
        endTime,
        pickupCapacity: vendor?.pickupCapacity ?? 0,
      });
      setDialogOpen(true);
    } catch (error: unknown) {
      console.error('Error creating one-click schedule:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to create schedule. Please check your location permissions.';
      openSnackbarWarning({
        message: errorMessage,
      });
    } finally {
      setIsBusy(false);
    }
  }, [
    getNearestPastHalfHour,
    openSnackbarWarning,
    requestLocation,
    reverseGeocode,
    vendor?.pickupCapacity,
  ]);

  const handleCreateSchedule = useCallback(async () => {
    setIsBusy(true);
    try {
      // Check permission status first
      const hasPermission = await checkAndRequestPermission();

      if (!hasPermission) {
        // Permission was denied or unsupported, show alert
        setIsBusy(false);
        return;
      }

      // Show prompt alert asking user to allow or open wizard
      const shouldAllow = await getConfirmation({
        cancelButtonText: 'Open Wizard',
        confirmButtonText: 'Allow',
        title: 'Location Permission Required',
        message: (
          <FlexView direction="column" gap={16}>
            <Text as="body2" tag="span">
              To create a schedule at your current location, we need access to
              your location. Please grant location permission when prompted.
            </Text>
            <Text as="body2" tag="span">
              You can also manually enter your location using the schedule
              wizard if you prefer not to share your location.
            </Text>
          </FlexView>
        ),
      });

      if (shouldAllow) {
        // User clicked "Allow", request location
        await handleRequestLocationAndCreateSchedule();
      } else {
        // User clicked "Open Wizard"
        setIsBusy(false);
        handleOpenWizard();
      }
    } catch (error: unknown) {
      console.error('Error in handleCreateSchedule:', error);
      setIsBusy(false);
    }
  }, [
    checkAndRequestPermission,
    getConfirmation,
    handleOpenWizard,
    handleRequestLocationAndCreateSchedule,
  ]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleModify = useCallback(() => {
    handleCloseDialog();

    wizardDrawerRef.current?.open();
  }, [handleCloseDialog]);

  const handleChangeStep = useCallback(
    (step: number) => {
      handleCloseDialog();
      setWizardInitialStep(step);
      wizardDrawerRef.current?.open();
    },
    [handleCloseDialog]
  );

  const submitSchedule = useCallback(
    async (data: OneClickScheduleData) => {
      const { location, pickupCapacity, startTime, endTime } = data;

      try {
        const scheduleData = {
          days: [
            {
              display: `${dateUtil.format(
                startTime,
                config.dateFormats.weekDayMonthDayYearShort
              )} from ${dateUtil.getTimeDisplay(
                startTime
              )}-${dateUtil.getTimeDisplay(endTime)}`,
              end: endTime,
              start: startTime,
              location,
            },
          ],
          source: 'Manual Entry' as const,
          menuId: vendor?.defaultMenu,
          isPrivate: false,
          pickupCapacity,
          ...location,
        };

        await createSchedule(scheduleData);
      } catch (error) {
        console.error('Error submitting schedule:', error);
        throw error;
      }
    },
    [vendor?.defaultMenu, createSchedule]
  );

  const handleConfirm = useCallback(async () => {
    if (!scheduleData) {
      return;
    }

    await submitSchedule(scheduleData);
  }, [scheduleData, submitSchedule]);

  return (
    <>
      <Button
        busy={isBusy || isCreatingSchedule}
        className={className}
        disabled={isBusy || isCreatingSchedule}
        onClick={handleCreateSchedule}
      >
        One-Click Create Schedule
      </Button>

      {scheduleData && (
        <OneClickSchedulePreview
          busy={isCreatingSchedule}
          data={scheduleData}
          onChangeStep={handleChangeStep}
          onClose={handleCloseDialog}
          onConfirm={handleConfirm}
          onModify={handleModify}
          open={dialogOpen}
          setPickupCapacity={(pickupCapacity) => {
            setScheduleData({ ...scheduleData, pickupCapacity });
          }}
        />
      )}

      <ScheduleWizardDrawer
        hideTarget
        ref={wizardDrawerRef}
        wizardProps={{
          initialData: scheduleData
            ? {
                dates: [scheduleData.startTime],
                location: scheduleData.location,
                start: scheduleData.startTime,
                end: scheduleData.endTime,
              }
            : undefined,
          initialStep: wizardInitialStep,
          vendorId: vendor?.id,
          onExit: () => {
            wizardDrawerRef.current?.close();
          },
        }}
      />
    </>
  );
}
